import { useMemo, useState } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { captureMessage } from "@sentry/nextjs";

export type GQLOrderByEnumsById<OrderByEnum> = {
  [id: string]: [asc: OrderByEnum, desc: OrderByEnum] | undefined;
};

export default function useGQLSorting<OrderByEnum>(
  gqlOrderByEnumsById: GQLOrderByEnumsById<OrderByEnum>,
  defaultSorting: SortingState = [],
) {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);

  const orderBy = sorting.reduce<OrderByEnum[]>((acc, { id, desc }) => {
    const gqlValue = gqlOrderByEnumsById[id];

    if (!gqlValue) {
      const msg = `Received a sorting id (${id}) that cannot be converted to a GraphQL value!`;
      // eslint-disable-next-line no-console
      console.warn(msg);
      captureMessage(msg, { extra: { id, desc, sorting } });
      return acc;
    }

    acc.push(gqlValue[desc ? 1 : 0]);

    return acc;
  }, []);

  return { sorting, onSortingChange: setSorting, orderBy };
}

export function setSortingEnabledForColumns<D>(
  gqlOrderByEnumsById: GQLOrderByEnumsById<unknown>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<D, any>[],
) {
  const enabledColumnIds = Object.keys(gqlOrderByEnumsById);
  return columns.map((col) => ({
    ...col,
    enableSorting: col.id ? enabledColumnIds.includes(col.id) : undefined,
  }));
}

export function useSetSortingEnabledForColumns<D>(
  gqlOrderByEnumsById: GQLOrderByEnumsById<unknown>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<D, any>[],
) {
  return useMemo(
    () => setSortingEnabledForColumns<D>(gqlOrderByEnumsById, columns),
    [gqlOrderByEnumsById, columns],
  );
}
